/* Angular Component */
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  SimpleChanges,
} from "@angular/core";
import { UI } from "../../../services/ui.service";

const ESC_KEY_CODE = 27;
const MESSAGE_FADE_MS = 250;

@Component({
  selector: "alert",
  templateUrl: "./alert.component.html",
  styleUrls: ["./alert.component.scss"],
})
export class AlertComponent implements OnDestroy {
  @Input()
  active = false;

  @Input()
  alertObject: IAlertObjectInterface = {
    type: "alert",
    title: "",
    message: "",
    callback: undefined,
    options: null,
    buttons: [],
    bottomTexts: [],
    display: false,
  };

  isProcessing = false;
  displayMessage = "";
  messageVisible = true;

  private progressTimeouts: ReturnType<typeof setTimeout>[] = [];
  private escCallback;

  constructor(
    private ui: UI,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.escCallback = this.escDialog.bind(this);
  }

  /**
   * On Change Callback
   * @param changes
   * @return void
   */
  ngOnChanges(changes: SimpleChanges) {
    /* Validate active */
    if (!changes["active"]) {
      return;
    }

    /* In case of activating the value */
    if (changes["active"].currentValue) {
      this.cleanupProgress();
      this.dialogDidAppear();
    }
  }

  ngOnDestroy() {
    this.cleanupProgress();
    this.dialogDidDisappear();
  }

  /**
   * Dialog Did appear
   * @return void
   */
  dialogDidAppear() {
    window.addEventListener("keyup", this.escCallback, false);
  }

  /**
   * Disappear dialog callback
   * @return void
   */
  dialogDidDisappear() {
    window.removeEventListener("keyup", this.escCallback, false);
  }

  /**
   * escaping dialog
   * @param event
   */
  escDialog(event) {
    if (this.isProcessing) {
      return;
    }

    /* Quit on esc */
    if (event.keyCode && event.keyCode == ESC_KEY_CODE) {
      this.done(this.alertObject.callback)();

      this.dialogDidDisappear();
    }
  }

  onBackdropClick() {
    if (this.isProcessing) {
      return;
    }

    this.done(undefined)();
  }

  private startProgress(button: IUIServiceButton) {
    const steps = button.progressSteps;
    if (!steps?.length) {
      return;
    }

    this.isProcessing = true;
    this.displayMessage = steps[0].message;
    this.messageVisible = true;
    this.changeDetectorRef.detectChanges();

    let cumulativeDelay = 0;
    for (let i = 0; i < steps.length - 1; i++) {
      const step = steps[i];
      if (step.duration <= 0) {
        continue;
      }

      cumulativeDelay += step.duration;
      const nextMessage = steps[i + 1].message;
      const timeoutId = setTimeout(() => {
        this.transitionToMessage(nextMessage);
      }, cumulativeDelay);
      this.progressTimeouts.push(timeoutId);
    }
  }

  private transitionToMessage(message: string) {
    this.messageVisible = false;
    const swapTimeoutId = setTimeout(() => {
      this.displayMessage = message;
      this.messageVisible = true;
      this.changeDetectorRef.detectChanges();
    }, MESSAGE_FADE_MS);
    this.progressTimeouts.push(swapTimeoutId);
  }

  private cleanupProgress() {
    for (const timeoutId of this.progressTimeouts) {
      clearTimeout(timeoutId);
    }
    this.progressTimeouts = [];
    this.isProcessing = false;
    this.displayMessage = "";
    this.messageVisible = true;
  }

  /**
   * Main Listener
   * @param $event
   * @TODO: Finish callback
   */
  done(callback, button?: IUIServiceButton) {
    return (async () => {
      try {
        if (button?.progressSteps?.length) {
          this.startProgress(button);
        }

        /* Dispatch Notification */
        if (typeof callback === "function") {
          await callback();
        }
      } finally {
        this.cleanupProgress();

        /* Notify Alert Backbone */
        this.ui.notify(this.ui.EVENTS.AlertDismissed, {});

        /* Immediate Visual Change */
        this.active = false;
      }
    }).bind(this);
  }
}
export class AlertParagraphText {
  constructor(
    public textHtml: string,
    public styleObj: any = {},
  ) {}
}
export interface IAlertObjectInterface {
  title: string;
  message: string;
  callback: undefined;
  type: string;
  display: boolean;
  options: { id: number; displayName: string; selected: boolean }[];
  buttons: IUIServiceButton[];
  bottomTexts: AlertParagraphText[];
}

export interface IAlertProgressStep {
  message: string;
  duration: number;
}

export interface IUIServiceButton {
  title: string;
  callback(): void;
  theme?: string;
  disabled?: boolean;
  progressSteps?: IAlertProgressStep[];
}
